import OriginalTable from "@editorjs/table";

const BRAND_COLORS = [
  "#1BACE4", "#1589B6", "#004C7F", "#1e3a5f",
  "#32A728", "#E83F1E", "#ff9800", "#ff6b35",
  "#333333", "#666666", "#999999", "#FFFFFF",
];

const BORDER_STYLES = ["solid", "dashed", "dotted", "none"];
const BORDER_WIDTHS = ["1px", "2px", "3px"];
// Minimum width reserved per column. Once the columns no longer fit the
// editor, the table grows wider and the editor container shows a
// horizontal scrollbar instead of squashing the existing columns.
const MIN_COL_WIDTH = 150;
const ADD_COLUMN_TRACK_WIDTH = 34;

class CustomTableTool extends OriginalTable {
  constructor(args) {
    super(args);
    const d = args.data || {};
    this._colWidths = d.colWidths || [];
    this._rowHeights = d.rowHeights || [];
    this._headerBgColor = d.headerBgColor || "";
    this._cellBgColors = d.cellBgColors || [];
    this._borderColor = d.borderColor || "#ddd";
    this._borderColorCustomized = d.borderColorCustomized ?? (!!d.borderColor && d.borderColor !== "#ddd");
    this._borderWidth = d.borderWidth || "1px";
    this._borderStyle = d.borderStyle || "solid";
    this._settingsPanel = null;
    this._activeCell = null;
  }

  static get sanitize() {
    return {
      br: true,
      b: true,
      i: true,
      a: {
        href: true,
        target: "_blank",
        rel: "noopener noreferrer",
      },
      mark: { class: true },
      code: true,
      u: true,
      font: {
        color: true,
        style: true,
      },
      style: true,
    };
  }

  render() {
    const wrapper = super.render();
    if (!wrapper) return wrapper;

    this._wrapperEl = wrapper;
    this._patchToolboxesPosition();
    this._patchColumnOperations();
    setTimeout(() => this._applyTableStyles(wrapper), 0);

    return wrapper;
  }

  _patchToolboxesPosition() {
    // Plugin centers the column toggler assuming equal-width columns; patch
    // the internal Table instance to position it from the real cell rect.
    const tableApi = this.table;
    if (!tableApi || typeof tableApi.updateToolboxesPosition !== "function") return;

    const original = tableApi.updateToolboxesPosition.bind(tableApi);
    tableApi.updateToolboxesPosition = (row = tableApi.hoveredRow, col = tableApi.hoveredColumn) => {
      original(row, col);
      if (!col || col < 1 || col > tableApi.numberOfColumns) return;
      const toolboxEl = tableApi.toolboxColumn?.element;
      const wrap = tableApi.getWrapper();
      const cell = tableApi.getCell(1, col);
      if (!toolboxEl || !wrap || !cell) return;
      const wrapRect = wrap.getBoundingClientRect();
      const cellRect = cell.getBoundingClientRect();
      toolboxEl.style.left = `${
        cellRect.left - wrapRect.left - (wrap.clientLeft || 0) + cellRect.width / 2
      }px`;
    };
  }

  _patchColumnOperations() {
    // Keep per-column state arrays aligned when columns are inserted/removed,
    // whether via the native plugin toolbox or our custom toolbar. This ensures
    // the grid-template-columns and cell background colors stay in sync with
    // the real DOM. The patch is attached to the internal table instance because
    // super.render() may create a new one on each Editor.js block render.
    const tableApi = this.table;
    if (!tableApi || tableApi._customOpsPatched) return;
    tableApi._customOpsPatched = true;

    const originalAddColumn = tableApi.addColumn.bind(tableApi);
    tableApi.addColumn = (index, setFocus) => {
      const numCols = tableApi.numberOfColumns;
      const insertAt = index > 0 && index <= numCols ? index - 1 : numCols;
      originalAddColumn(index, setFocus);
      this._colWidths.splice(insertAt, 0, undefined);
      this._cellBgColors.forEach((row) => {
        if (row) row.splice(insertAt, 0, undefined);
      });
    };

    const originalDeleteColumn = tableApi.deleteColumn.bind(tableApi);
    tableApi.deleteColumn = (index) => {
      originalDeleteColumn(index);
      if (index >= 1) {
        const deleteAt = index - 1;
        this._colWidths.splice(deleteAt, 1);
        this._cellBgColors.forEach((row) => {
          if (row) row.splice(deleteAt, 1);
        });
      }
    };
  }

  _applyTableStyles(wrapper) {
    const tableEl = wrapper.querySelector ? (
      wrapper.classList?.contains("tc-table") ? wrapper : wrapper.querySelector(".tc-table")
    ) : null;
    if (!tableEl) return;

    this._tableEl = tableEl;

    // Only apply inline border if user explicitly changed it from defaults
    if (this._borderColor !== "#ddd" || this._borderWidth !== "1px" || this._borderStyle !== "solid") {
      const cells = tableEl.querySelectorAll(".tc-cell");
      cells.forEach((cell) => {
        cell.style.border = `${this._borderWidth} ${this._borderStyle} ${this._borderColor}`;
      });
    }

    // Apply header background
    if (this._headerBgColor) {
      const rows = tableEl.querySelectorAll(".tc-row");
      const withHeadings = this.data ? !!this.data.withHeadings : true;
      if (rows.length > 0 && withHeadings) {
        const headerCells = rows[0].querySelectorAll(".tc-cell");
        headerCells.forEach((cell) => {
          cell.style.backgroundColor = this._headerBgColor;
          cell.style.color = this._isLightColor(this._headerBgColor) ? "#333" : "#fff";
          cell.style.fontWeight = "bold";
        });
      }
    }

    // Apply column widths via grid-template-columns on every row
    // (each .tc-row is an independent CSS grid, so per-cell width styles
    // conflict with the grid track sizing algorithm; we must set the
    // column track sizes uniformly on every row instead)
    this._applyColumnWidths(tableEl);

    // Apply cell background colors
    if (this._cellBgColors && this._cellBgColors.length > 0) {
      const rows = tableEl.querySelectorAll(".tc-row");
      rows.forEach((row, rIdx) => {
        const rowCells = row.querySelectorAll(".tc-cell");
        const rowColors = this._cellBgColors[rIdx] || [];
        rowCells.forEach((cell, cIdx) => {
          const color = rowColors[cIdx];
          if (color) {
            cell.style.backgroundColor = color;
            // Border toolbar is disabled for now — match the cell border to its fill color
            cell.style.border = `${this._borderWidth} ${this._borderStyle} ${color}`;
          }
        });
      });
    }

    // Apply row heights
    this._applyRowHeights(tableEl);

    // Add column resize handles
    this._addResizeHandles(tableEl);

    // Add row resize handles
    this._addRowResizeHandles(tableEl);

    // Add cell click listener for cell background
    this._addCellListeners(tableEl, wrapper);

    // Add custom toolbar
    this._addCustomToolbar(tableEl, wrapper);

    // Reserve a minimum width per column so the table overflows
    // (horizontal scroll) rather than distorting when columns are added
    this._updateMinTableWidth(tableEl, wrapper);
    this._observeTableStructure(tableEl, wrapper);
  }

  _updateMinTableWidth(tableEl, wrapper) {
    if (!tableEl || !wrapper?.style) return;
    const firstRow = tableEl.querySelector(".tc-row");
    if (!firstRow) return;
    const numCols = firstRow.querySelectorAll(".tc-cell").length;
    if (numCols === 0) return;
    wrapper.style.minWidth = `${numCols * MIN_COL_WIDTH + ADD_COLUMN_TRACK_WIDTH}px`;
  }

  _observeTableStructure(tableEl, wrapper) {
    if (this._tableObserver) this._tableObserver.disconnect();
    this._tableObserver = new MutationObserver(() => {
      // A row's grid-template-columns is an explicit track list (one track
      // per column). When a column is added/removed the extra cells would
      // wrap into implicit grid rows and distort the table, so rebuild the
      // track list to match the new column count first.
      this._applyColumnWidths(tableEl);
      this._updateMinTableWidth(tableEl, wrapper);
    });
    this._tableObserver.observe(tableEl, { childList: true, subtree: true });
  }

  destroyed() {
    if (this._tableObserver) {
      this._tableObserver.disconnect();
      this._tableObserver = null;
    }
    if (typeof super.destroyed === "function") super.destroyed();
  }

  _applyRowHeights(tableEl) {
    const rows = tableEl.querySelectorAll(".tc-row");
    rows.forEach((row, idx) => {
      const h = this._rowHeights[idx];
      row.style.minHeight = h || "";
    });
  }

  _applyColumnWidths(tableEl) {
    const rows = tableEl.querySelectorAll(".tc-row");
    if (rows.length === 0) return;
    const numCols = rows[0].querySelectorAll(".tc-cell").length;
    if (numCols === 0) return;

    // Reset stored widths whenever the column count changes, so inserting or
    // removing a column doesn't leave stale widths on the wrong columns.
    if (this._lastNumCols && this._lastNumCols !== numCols) {
      this._colWidths = new Array(numCols).fill(undefined);
    }
    this._lastNumCols = numCols;

    const parts = [];
    for (let i = 0; i < numCols; i++) {
      const width = this._colWidths[i];
      const hasValidWidth =
        width && String(width).trim() && parseFloat(width) > 0;
      parts.push(
        hasValidWidth
          ? `minmax(${MIN_COL_WIDTH}px, ${width})`
          : `minmax(${MIN_COL_WIDTH}px, 1fr)`
      );
    }
    const template = parts.join(" ");

    rows.forEach((row) => {
      row.style.gridTemplateColumns = template;
      row.querySelectorAll(".tc-cell").forEach((cell) => {
        // Clear any stale per-cell width overrides from earlier versions and
        // make sure long variable placeholders don't wrap character by character.
        cell.style.width = "";
        cell.style.minWidth = "";
        cell.style.maxWidth = "";
        cell.style.wordBreak = "normal";
        cell.style.overflowWrap = "break-word";
      });
    });
  }

  // Normalize a color string to a full 6-digit hex value usable by <input type="color">.
  // Returns null if the color can't be represented as hex (e.g. named colors, rgb()).
  _toValidHexColor(color) {
    if (!color) return null;
    let c = color.trim();
    if (!c.startsWith("#")) return null;
    if (c.length === 4) {
      // Expand shorthand #rgb -> #rrggbb
      c = "#" + c.slice(1).split("").map((ch) => ch + ch).join("");
    }
    return c.length === 7 ? c.toLowerCase() : null;
  }

  // Suggest a border color based on whatever fill color is currently applied:
  // active cell background -> header background -> any cell background -> null
  _getSuggestedFillColor() {
    if (this._activeRowIndex != null && this._activeCellIndex != null) {
      const c = this._cellBgColors[this._activeRowIndex]?.[this._activeCellIndex];
      const hex = this._toValidHexColor(c);
      if (hex) return hex;
    }
    const headerHex = this._toValidHexColor(this._headerBgColor);
    if (headerHex) return headerHex;
    for (const row of this._cellBgColors) {
      if (!row) continue;
      for (const c of row) {
        const hex = this._toValidHexColor(c);
        if (hex) return hex;
      }
    }
    return null;
  }

  _isLightColor(hex) {
    if (!hex || !hex.startsWith("#")) return false;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 155;
  }

  _addResizeHandles(tableEl) {
    // Remove all existing handles first to prevent accumulation
    tableEl.querySelectorAll(".tc-col-resize-handle").forEach((h) => h.remove());

    const rows = tableEl.querySelectorAll(".tc-row");
    if (rows.length === 0) return;
    const firstRowCells = rows[0].querySelectorAll(".tc-cell");
    const numCols = firstRowCells.length;

    for (let i = 0; i < numCols - 1; i++) {
      const cell = firstRowCells[i];
      if (!cell) continue;

      const handle = document.createElement("div");
      handle.style.cssText = `
        position: absolute;
        top: 0;
        right: -3px;
        width: 6px;
        height: 100%;
        cursor: col-resize;
        z-index: 10;
        background: transparent;
        pointer-events: auto;
      `;
      handle.classList.add("tc-col-resize-handle");
      // The handle lives inside a contenteditable cell; without this the caret
      // lands inside the 6px-wide handle on empty cells and typed text stacks there.
      handle.contentEditable = "false";
      handle.style.userSelect = "none";

      if (getComputedStyle(cell).position === "static") {
        cell.style.position = "relative";
      }

      handle.addEventListener("mousedown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this._startColumnResize(tableEl, i, e);
      });

      cell.appendChild(handle);
    }
  }

  _startColumnResize(tableEl, colIndex, startEvent) {
    const rows = tableEl.querySelectorAll(".tc-row");
    const firstRowCells = rows[0].querySelectorAll(".tc-cell");
    const currentCell = firstRowCells[colIndex];
    const nextCell = firstRowCells[colIndex + 1];
    if (!currentCell || !nextCell) return;

    const startX = startEvent.clientX;
    const startCurrentWidth = currentCell.offsetWidth;
    const startNextWidth = nextCell.offsetWidth;
    const tableWidth = tableEl.offsetWidth;
    const combinedWidth = startCurrentWidth + startNextWidth;

    const onMouseMove = (e) => {
      e.preventDefault();
      const delta = e.clientX - startX;
      const newCurrentWidth = Math.max(40, Math.min(combinedWidth - 40, startCurrentWidth + delta));
      const newNextWidth = combinedWidth - newCurrentWidth;

      const currentPercent = `${((newCurrentWidth / tableWidth) * 100).toFixed(1)}%`;
      const nextPercent = `${((newNextWidth / tableWidth) * 100).toFixed(1)}%`;

      this._colWidths[colIndex] = currentPercent;
      this._colWidths[colIndex + 1] = nextPercent;

      // Apply the updated grid-template-columns uniformly to every row
      this._applyColumnWidths(tableEl);
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  _addRowResizeHandles(tableEl) {
    // Remove all existing handles first to prevent accumulation
    tableEl.querySelectorAll(".tc-row-resize-handle").forEach((h) => h.remove());

    const rows = tableEl.querySelectorAll(".tc-row");

    rows.forEach((row, idx) => {
      const handle = document.createElement("div");
      handle.style.cssText = `
        position: absolute;
        left: 0;
        bottom: -3px;
        width: 100%;
        height: 6px;
        cursor: row-resize;
        z-index: 10;
        background: transparent;
        pointer-events: auto;
      `;
      handle.classList.add("tc-row-resize-handle");
      handle.contentEditable = "false";
      handle.style.userSelect = "none";

      if (getComputedStyle(row).position === "static") {
        row.style.position = "relative";
      }

      handle.addEventListener("mousedown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this._startRowResize(tableEl, idx, e);
      });

      row.appendChild(handle);
    });
  }

  _startRowResize(tableEl, rowIndex, startEvent) {
    const rows = tableEl.querySelectorAll(".tc-row");
    const row = rows[rowIndex];
    if (!row) return;

    const startY = startEvent.clientY;
    const startHeight = row.offsetHeight;

    const onMouseMove = (e) => {
      e.preventDefault();
      const delta = e.clientY - startY;
      const newHeight = Math.max(30, startHeight + delta);
      const heightPx = `${newHeight}px`;

      this._rowHeights[rowIndex] = heightPx;
      row.style.minHeight = heightPx;
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  _addCellListeners(tableEl, wrapper) {
    // Delegate from the table element instead of attaching per-cell
    // listeners: cells added later (new row/column) are covered too.
    if (tableEl._cellListenersAttached) return;
    tableEl._cellListenersAttached = true;

    const updateActive = (e) => {
      const cell = e.target.closest ? e.target.closest(".tc-cell") : null;
      if (!cell || !tableEl.contains(cell)) return;

      this._activeCell = cell;
      this._activeCellIndex = Array.from(
        cell.parentElement.querySelectorAll(".tc-cell")
      ).indexOf(cell);
      this._activeRowIndex = Array.from(
        tableEl.querySelectorAll(".tc-row")
      ).indexOf(cell.parentElement);

      // Float the toolbar just above the clicked cell instead of a fixed corner
      if (this._toolbarEl) {
        this._positionToolbarAtCell(this._toolbarEl, wrapper, cell);
      }
    };
    tableEl.addEventListener("focusin", updateActive);
    tableEl.addEventListener("mousedown", updateActive);
    tableEl.addEventListener("click", updateActive);
  }

  _positionToolbarAtCell(toolbar, wrapper, cell) {
    // Show it first so offsetHeight reflects the real rendered size, not the
    // hidden (display: none) size which is always 0.
    toolbar.style.display = "flex";

    // The toolbar is appended to document.body (not inside the scrollable
    // .tc-wrap), so position it purely from the cell's viewport rect + page
    // scroll — this avoids touching the table's own horizontal scroll container,
    // which previously caused its scrollLeft to reset unexpectedly.
    const cellRect = cell.getBoundingClientRect();
    const toolbarHeight = toolbar.offsetHeight;
    const gap = 6;

    let top = cellRect.top + window.scrollY - toolbarHeight - gap;
    if (top < window.scrollY) {
      // Not enough room above (e.g. the first row) — place it below the cell instead
      top = cellRect.bottom + window.scrollY + gap;
    }
    const left = Math.max(0, cellRect.left + window.scrollX);

    toolbar.style.top = `${top}px`;
    toolbar.style.left = `${left}px`;
    toolbar.style.right = "auto";
  }

  _addCustomToolbar(tableEl, wrapper) {
    // Remove any existing toolbar (it lives on document.body, not inside wrapper)
    const existingGlobal = document.querySelector(".tc-custom-toolbar");
    if (existingGlobal) existingGlobal.remove();

    const toolbar = document.createElement("div");
    toolbar.classList.add("tc-custom-toolbar");
    toolbar.style.cssText = `
      position: absolute;
      top: 2px;
      right: 2px;
      display: none;
      gap: 2px;
      padding: 3px;
      background: #fff;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.12);
      align-items: center;
      z-index: 100;
      white-space: nowrap;
    `;

    const makeBtn = (svg, title) => {
      const btn = document.createElement("div");
      btn.style.cssText = `
        display: flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        cursor: pointer;
        border-radius: 4px;
        color: #555;
        transition: background 0.15s;
      `;
      btn.innerHTML = svg;
      btn.title = title;
      btn.addEventListener("mouseenter", () => { btn.style.background = "#f0f0f0"; });
      btn.addEventListener("mouseleave", () => { btn.style.background = "transparent"; });
      return btn;
    };

    // --- Cell background color ---
    // Border toolbar is disabled for now; the selected cell's border is
    // automatically matched to whatever fill color is applied here.
    const cellBgBtn = makeBtn(
      `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/><rect x="8" y="8" width="8" height="8" rx="1" fill="currentColor" opacity="0.5"/></svg>`,
      "Cell background color (click a cell first, then click this)"
    );
    cellBgBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (this._activeRowIndex == null || this._activeCellIndex == null) {
        alert("Please click inside a table cell first to select it, then click this button to change its background color.");
        return;
      }
      this._showColorPicker(cellBgBtn, (color) => {
        if (!this._cellBgColors[this._activeRowIndex]) {
          this._cellBgColors[this._activeRowIndex] = [];
        }
        this._cellBgColors[this._activeRowIndex][this._activeCellIndex] = color;
        this._applyTableStyles(wrapper);
      });
    });
    toolbar.appendChild(cellBgBtn);

    // --- Column actions (shown in the same toolbar as the color option) ---
    const divider = document.createElement("span");
    divider.style.cssText = "width:1px;height:18px;background:#e0e0e0;margin:0 2px;";
    toolbar.appendChild(divider);

    const requireCell = () => {
      if (this._activeRowIndex == null || this._activeCellIndex == null) {
        alert("Please click inside a table cell first to select the column.");
        return false;
      }
      return true;
    };

    const addColLeftBtn = makeBtn(
      `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14 5v14M4 12h6M7 9l-3 3 3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M17 5h3v14h-3z" stroke="currentColor" stroke-width="1.5"/></svg>`,
      "Add column to the left"
    );
    addColLeftBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (!requireCell()) return;
      const at = this._activeCellIndex; // 0-based insertion point
      this.table.addColumn(at + 1); // plugin is 1-based, inserts before that index
      this._applyTableStyles(wrapper);
    });
    toolbar.appendChild(addColLeftBtn);

    const addColRightBtn = makeBtn(
      `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 5v14M14 12h6M17 9l3 3-3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 5h3v14H4z" stroke="currentColor" stroke-width="1.5"/></svg>`,
      "Add column to the right"
    );
    addColRightBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (!requireCell()) return;
      const at = this._activeCellIndex + 1; // insert after the active column
      this.table.addColumn(at + 1);
      this._applyTableStyles(wrapper);
    });
    toolbar.appendChild(addColRightBtn);

    const delColBtn = makeBtn(
      `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="4" y="5" width="16" height="14" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`,
      "Delete this column"
    );
    delColBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (!requireCell()) return;
      if (this.table.numberOfColumns <= 1) return;
      const at = this._activeCellIndex;
      this.table.deleteColumn(at + 1);
      this._activeCellIndex = null;
      this._activeRowIndex = null;
      this._applyTableStyles(wrapper);
    });
    toolbar.appendChild(delColBtn);

    // Append the toolbar to document.body (not inside the scrollable .tc-wrap)
    // so it never participates in that container's layout/scrollWidth, which
    // was previously causing the table's horizontal scroll to reset.
    document.body.appendChild(toolbar);
    this._toolbarEl = toolbar;

    // Hide toolbar when clicking outside the table or the toolbar itself
    const hideToolbar = () => {
      if (document.querySelector(".tc-color-picker-popup") || document.querySelector(".tc-border-panel-popup")) return;
      toolbar.style.display = "none";
    };

    document.addEventListener("click", (e) => {
      if (!wrapper.contains(e.target) && !toolbar.contains(e.target)) {
        hideToolbar();
      }
    });
  }

  removed() {
    if (this._toolbarEl) {
      this._toolbarEl.remove();
      this._toolbarEl = null;
    }
    if (typeof super.removed === "function") {
      super.removed();
    }
  }

  _showColorPicker(anchor, callback) {
    // Remove any existing picker
    const existing = document.querySelector(".tc-color-picker-popup");
    if (existing) existing.remove();

    const popup = document.createElement("div");
    popup.classList.add("tc-color-picker-popup");
    popup.style.cssText = `
      position: absolute;
      z-index: 1000;
      background: white;
      border: 1px solid #ccc;
      border-radius: 6px;
      padding: 10px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      width: 180px;
    `;

    // Brand color swatches
    BRAND_COLORS.forEach((color) => {
      const swatch = document.createElement("div");
      swatch.style.cssText = `
        width: 24px;
        height: 24px;
        border-radius: 4px;
        cursor: pointer;
        border: 1px solid #ddd;
        background: ${color};
      `;
      swatch.addEventListener("click", () => {
        callback(color);
        popup.remove();
      });
      popup.appendChild(swatch);
    });

    // Custom color input
    const customRow = document.createElement("div");
    customRow.style.cssText = `display: flex; gap: 6px; margin-top: 6px; width: 100%;`;

    const colorInput = document.createElement("input");
    colorInput.type = "color";
    colorInput.value = "#1BACE4";
    colorInput.style.cssText = `width: 32px; height: 28px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer;`;

    const hexInput = document.createElement("input");
    hexInput.type = "text";
    hexInput.value = "#1BACE4";
    hexInput.placeholder = "#RRGGBB";
    hexInput.style.cssText = `flex: 1; padding: 4px 8px; border: 1px solid #ddd; border-radius: 4px; font-size: 12px;`;

    const applyBtn = document.createElement("button");
    applyBtn.textContent = "OK";
    applyBtn.style.cssText = `padding: 4px 10px; border: 1px solid #1BACE4; background: #1BACE4; color: white; border-radius: 4px; cursor: pointer; font-size: 12px;`;
    applyBtn.addEventListener("click", () => {
      const val = hexInput.value.trim();
      if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
        callback(val);
        popup.remove();
      }
    });

    colorInput.addEventListener("input", () => {
      hexInput.value = colorInput.value;
    });

    customRow.appendChild(colorInput);
    customRow.appendChild(hexInput);
    customRow.appendChild(applyBtn);
    popup.appendChild(customRow);

    // Clear option
    const clearBtn = document.createElement("button");
    clearBtn.textContent = "Clear";
    clearBtn.style.cssText = `margin-top: 6px; width: 100%; padding: 4px; border: 1px solid #ddd; background: #f5f5f5; border-radius: 4px; cursor: pointer; font-size: 12px;`;
    clearBtn.addEventListener("click", () => {
      callback("");
      popup.remove();
    });
    popup.appendChild(clearBtn);

    // Position relative to anchor
    const rect = anchor.getBoundingClientRect();
    popup.style.top = `${rect.bottom + window.scrollY + 4}px`;
    popup.style.left = `${rect.left + window.scrollX}px`;

    document.body.appendChild(popup);

    // Clamp horizontally so the whole popup (including the Apply button)
    // stays within the viewport instead of overflowing off-screen.
    const popupRect = popup.getBoundingClientRect();
    const margin = 8;
    if (popupRect.right > window.innerWidth - margin) {
      const overflow = popupRect.right - (window.innerWidth - margin);
      popup.style.left = `${parseFloat(popup.style.left) - overflow}px`;
    }
    if (popupRect.left < margin) {
      popup.style.left = `${margin + window.scrollX}px`;
    }

    // Close on outside click
    setTimeout(() => {
      const onDocClick = (e) => {
        if (!popup.contains(e.target) && e.target !== anchor) {
          popup.remove();
          document.removeEventListener("click", onDocClick);
        }
      };
      document.addEventListener("click", onDocClick);
    }, 0);
  }

  _showBorderPanel(anchor) {
    const existing = document.querySelector(".tc-border-panel-popup");
    if (existing) existing.remove();

    const popup = document.createElement("div");
    popup.classList.add("tc-border-panel-popup");
    popup.style.cssText = `
      position: absolute;
      z-index: 1000;
      background: white;
      border: 1px solid #ccc;
      border-radius: 6px;
      padding: 12px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 200px;
    `;

    const makeRow = (label, control) => {
      const row = document.createElement("div");
      row.style.cssText = `display: flex; align-items: center; justify-content: space-between; gap: 8px;`;
      const lbl = document.createElement("span");
      lbl.textContent = label;
      lbl.style.cssText = `font-size: 12px; color: #555;`;
      row.appendChild(lbl);
      row.appendChild(control);
      return row;
    };

    // Border color — auto-suggest from the current fill color (active cell / header /
    // any cell background) if the border color hasn't been explicitly customized yet.
    // The user can still override it via this input.
    const currentHex = this._toValidHexColor(this._borderColor);
    const suggestedHex = this._borderColorCustomized
      ? currentHex
      : (this._getSuggestedFillColor() || currentHex);

    const colorInput = document.createElement("input");
    colorInput.type = "color";
    colorInput.value = suggestedHex || "#dddddd";
    colorInput.style.cssText = `width: 32px; height: 26px; border: 1px solid #ddd; border-radius: 4px; cursor: pointer; flex-shrink: 0;`;

    const hexInput = document.createElement("input");
    hexInput.type = "text";
    hexInput.value = suggestedHex || "#dddddd";
    hexInput.placeholder = "#RRGGBB";
    hexInput.style.cssText = `width: 80px; padding: 4px 6px; border: 1px solid #ddd; border-radius: 4px; font-size: 12px;`;

    colorInput.addEventListener("input", () => {
      hexInput.value = colorInput.value;
    });
    hexInput.addEventListener("input", () => {
      const val = hexInput.value.trim();
      if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
        colorInput.value = val;
      }
    });

    const colorControls = document.createElement("div");
    colorControls.style.cssText = `display: flex; gap: 6px; align-items: center;`;
    colorControls.appendChild(colorInput);
    colorControls.appendChild(hexInput);
    popup.appendChild(makeRow("Color", colorControls));

    // Border width
    const widthSelect = document.createElement("select");
    BORDER_WIDTHS.forEach((w) => {
      const opt = document.createElement("option");
      opt.value = w;
      opt.textContent = w;
      if (w === this._borderWidth) opt.selected = true;
      widthSelect.appendChild(opt);
    });
    widthSelect.style.cssText = `padding: 2px 6px; border: 1px solid #ddd; border-radius: 4px; font-size: 12px;`;
    popup.appendChild(makeRow("Width", widthSelect));

    // Border style
    const styleSelect = document.createElement("select");
    BORDER_STYLES.forEach((s) => {
      const opt = document.createElement("option");
      opt.value = s;
      opt.textContent = s;
      if (s === this._borderStyle) opt.selected = true;
      styleSelect.appendChild(opt);
    });
    styleSelect.style.cssText = `padding: 2px 6px; border: 1px solid #ddd; border-radius: 4px; font-size: 12px;`;
    popup.appendChild(makeRow("Style", styleSelect));

    // Apply button
    const applyBtn = document.createElement("button");
    applyBtn.textContent = "Apply";
    applyBtn.style.cssText = `margin-top: 4px; padding: 6px; border: 1px solid #1BACE4; background: #1BACE4; color: white; border-radius: 4px; cursor: pointer; font-size: 12px;`;
    applyBtn.addEventListener("click", () => {
      this._borderColor = colorInput.value;
      this._borderColorCustomized = true;
      this._borderWidth = widthSelect.value;
      this._borderStyle = styleSelect.value;
      if (this._wrapperEl) {
        this._applyTableStyles(this._wrapperEl);
      }
      popup.remove();
    });
    popup.appendChild(applyBtn);

    // Position
    const rect = anchor.getBoundingClientRect();
    popup.style.top = `${rect.bottom + window.scrollY + 4}px`;
    popup.style.left = `${rect.left + window.scrollX}px`;

    document.body.appendChild(popup);

    setTimeout(() => {
      const onDocClick = (e) => {
        if (!popup.contains(e.target) && e.target !== anchor) {
          popup.remove();
          document.removeEventListener("click", onDocClick);
        }
      };
      document.addEventListener("click", onDocClick);
    }, 0);
  }

  async save(blockContent) {
    const data = await super.save(blockContent);
    return {
      ...data,
      colWidths: this._colWidths,
      rowHeights: this._rowHeights,
      headerBgColor: this._headerBgColor,
      cellBgColors: this._cellBgColors,
      borderColor: this._borderColor,
      borderColorCustomized: this._borderColorCustomized,
      borderWidth: this._borderWidth,
      borderStyle: this._borderStyle,
    };
  }
}

export default CustomTableTool;
